import { useUserInfo } from "@/api/user";
import { MessageInput, MessageInputRef } from "@/components/MessageInput";
import { ROUTES } from "@/constants/routes";
import { withMessageInputContext } from "@/context/MessageInputContext";
import { useOnDrop } from "@/dnd/OsFileDropLayer";
import * as analytics from "@/utils/analytics";
import React, { useRef, useState } from "react";

import { useMessageActions } from "@/hooks/useMessageActions";
import { route } from "@/utils/route";
import { PostMessageParams, ReferencedEntity } from "@cohesive/api-client";

import { HomeActivityFeed } from "@/components/HomeActivityFeed";
import { useAppNavigate } from "@/components/PaneRouter";
import { MessageInputControlSet } from "@/constants/message-input";
import {
  useSessionContext,
  withSessionProvider,
} from "@/context/SessionContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { cn } from "@/utils";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

const HomePage: React.FC = withSessionProvider(() => {
  const navigate = useNavigate();

  const { setCurrentSessionId } = useSessionContext();
  const [currentReferencedEntities, setCurrentReferencedEntities] = useState<
    ReferencedEntity[]
  >([]);

  const appNavigate = useAppNavigate();

  const { isLoading: isUserInfoLoading } = useUserInfo();

  const { data: userInfo } = useUserInfo();

  const containerRef = useRef<HTMLDivElement>(null);

  const lg = useBreakpoint("lg");

  const messageInputRef = useRef<MessageInputRef>(null);

  const {
    uploadedFiles,
    onAttachFile,
    onRemoveFile,
    onSendMessage,
    isLoading: isMessageActionLoading,
  } = useMessageActions({
    onFlowSessionCreated: () => {
      analytics.trackNewChatCreated();
    },
    onMessageSent: ({ flowSession }) => {
      if (lg) {
        appNavigate({
          pane: "rightPane",
          to: route(ROUTES.SESSIONS.SESSION, {
            sessionId: flowSession.id,
          }),
          options: {
            state: {
              allowNewChat: true,
            },
          },
        });
        setCurrentSessionId(undefined);
      } else {
        navigate(
          route(ROUTES.SESSIONS.SESSION, {
            sessionId: flowSession.id,
          }),
        );
        setCurrentSessionId(undefined);
      }
    },
  });

  useOnDrop(onAttachFile);

  const handleSend = async (params: PostMessageParams) => {
    if (params.content?.trim() === "") {
      return;
    }

    await onSendMessage({
      id: uuidv4(),
      content: params.content?.trim(),
      file_record_ids: params.file_record_ids,
      referenced_entities: currentReferencedEntities,
    });

    analytics.trackHomeMessageSent({
      content: params.content?.trim(),
      fileRecordIds: params.file_record_ids ?? [],
    });
  };

  if (isUserInfoLoading) {
    return null;
  }

  return (
    <>
      <title>{import.meta.env.VITE_DEFAULT_APP_TITLE}</title>
      <div
        className={cn(
          "flex flex-col items-center max-w-full relative z-9 justify-start lg:max-h-screen lg:overflow-hidden",
        )}
        ref={containerRef}
      >
        <div className="flex flex-col items-center w-full flex-1 lg:min-h-0">
          <div className="invisible h-0 lg:h-auto lg:visible flex flex-row space-between sm:max-w-[776px] w-full">
            <h1 className="text-2xl text-left font-semibold mb-5">
              {`Good ${
                new Date().getHours() < 12
                  ? "morning"
                  : new Date().getHours() < 18
                    ? "afternoon"
                    : "evening"
              }${userInfo?.user?.first_name ? `, ${userInfo?.user?.first_name}` : ""}`}
            </h1>
          </div>
          <MessageInput
            sendButtonDisabled={isMessageActionLoading}
            ref={messageInputRef}
            onSend={handleSend}
            onAttachFile={onAttachFile}
            onRemoveFile={onRemoveFile}
            attachedFiles={uploadedFiles}
            onReferencedEntitiesChange={setCurrentReferencedEntities}
            onMenuChange={() => {
              // Handle menu changes if needed
            }}
            autoFocus={lg}
            location="main"
            controlSet={MessageInputControlSet.DEFAULT}
            className="mb-0"
            classNames={{
              // The ComposerShell provides the framed surface (1.5px border +
              // rust focus ring); the container only constrains width.
              container: "w-full sm:max-w-[776px] mx-auto",
            }}
          />

          <HomeActivityFeed className="w-full flex-1 z-50" />
        </div>
      </div>
    </>
  );
});

export default withMessageInputContext(HomePage);
